<script setup lang="ts">
/**
 * `<UiSpinner>` — an indeterminate busy indicator.
 *
 * A rotating SVG arc rather than a CSS border trick, because a border spinner
 * is drawn from four differently-coloured edges and needs two colour tokens to
 * look right; this needs none — it is `currentColor` at two opacities.
 *
 * Under `prefers-reduced-motion` the rotation stops and the arc becomes a full
 * ring: still a visible "busy" mark, no vestibular trigger. It is never hidden
 * outright, because the user still needs to know something is happening.
 */

withDefaults(
  defineProps<{
    /** Any CSS length; `1.25em` scales with the surrounding text. */
    size?: string | number
    /** Accessible label. Set it when the spinner is the only busy signal. */
    label?: string
  }>(),
  { size: '1.25em' },
)
</script>

<template>
  <span
    class="ui-spinner"
    :style="{ width: typeof size === 'number' ? `${size}px` : size, height: typeof size === 'number' ? `${size}px` : size }"
    :role="label ? 'status' : undefined"
    :aria-label="label"
    :aria-hidden="label ? undefined : 'true'"
  >
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      <circle class="ui-spinner__track" cx="12" cy="12" r="9.5" stroke="currentColor" stroke-width="2.5" />
      <path
        class="ui-spinner__head"
        d="M12 2.5a9.5 9.5 0 0 1 9.5 9.5"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
      />
    </svg>
  </span>
</template>

<style scoped>
.ui-spinner {
  display: inline-block;
  flex: none;
  vertical-align: -0.15em;
  color: inherit;
}

.ui-spinner svg {
  inline-size: 100%;
  block-size: 100%;
  animation: ui-spinner-rotate 800ms linear infinite;
}

.ui-spinner__track {
  opacity: 0.25;
}

@keyframes ui-spinner-rotate {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .ui-spinner svg {
    animation: none;
  }

  /* No rotation means an arc reads as a broken circle rather than as motion,
     so the arc is dropped and the ring is shown at full strength instead. */
  .ui-spinner__track {
    opacity: 0.6;
  }

  .ui-spinner__head {
    display: none;
  }
}
</style>
